import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDeliveryAddress, fetchCountriesForDropdown, fetchStatesByCountry } from '../services/api.ts';
import { ArrowLeft, MapPin, Globe, Home, User, Mail, Star } from 'lucide-react';
import './AddDeliveryAddress.css';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface State {
  id: number;
  name: string;
  code: string;
  country_id: number;
}

const AddDeliveryAddress: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    address_line_1: '',
    address_line_2: '',
    town: '',
    state_id: 0,
    postcode: '',
    country_id: 0,
    is_default: false
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data || []);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      if (formData.country_id === 0) {
        setStates([]);
        return;
      }
      try {
        setLoadingStates(true);
        const data = await fetchStatesByCountry(formData.country_id);
        setStates(data || []);
      } catch (err) {
        console.error('Error loading states:', err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, [formData.country_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.user_id.trim()) {
      setError('Please enter a valid user ID');
      return;
    }

    if (formData.country_id === 0) {
      setError('Please select a country');
      return;
    }

    if (formData.state_id === 0) {
      setError('Please select a state');
      return;
    }

    try {
      setError(null);
      await createDeliveryAddress(formData);
      navigate('/dashboard/delivery-addresses');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to create delivery address';
      setError(errorMessage);
      console.error('Create error:', err.response?.data);
    }
  };

  return (
    <div className="add-delivery-address">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/delivery-addresses')}>
          <ArrowLeft size={20} />
          Back to Delivery Addresses
        </button>
        <h2>Add New Delivery Address</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Address Information</h3>
            
            <div className="form-group">
              <label>User ID *</label>
              <div className="input-with-icon">
                <User size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  placeholder="Enter user ID"
                  required
                />
              </div>
              <p className="field-hint">The UUID of the user this address belongs to</p>
            </div>

            <div className="form-group">
              <label>Address Line 1 *</label>
              <div className="input-with-icon">
                <Home size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.address_line_1}
                  onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                  placeholder="Street address, P.O. box, etc."
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <div className="input-with-icon">
                <Home size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.address_line_2}
                  onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Town/City *</label>
                <input
                  type="text"
                  value={formData.town}
                  onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                  placeholder="Enter town or city"
                  required
                />
              </div>
              <div className="form-group">
                <label>Postcode *</label>
                <div className="input-with-icon">
                  <Mail size={18} className="input-icon" />
                  <input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    placeholder="Enter postcode"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.country_id}
                  onChange={(e) => setFormData({ ...formData, country_id: parseInt(e.target.value), state_id: 0 })}
                  required
                >
                  <option value={0}>Select a country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>State/Province *</label>
                <select
                  value={formData.state_id}
                  onChange={(e) => setFormData({ ...formData, state_id: parseInt(e.target.value) })}
                  required
                  disabled={!formData.country_id || loadingStates}
                >
                  <option value={0}>
                    {loadingStates ? 'Loading states...' : 'Select a state'}
                  </option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <p className="field-hint">Select a country first to load states</p>
              </div>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Default Address</label>
                  <p className="switch-description">Set this as the user's default delivery address</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon">
                  <Home size={24} />
                </div>
                {formData.is_default && (
                  <span className="preview-default-badge">
                    <Star size={14} />
                    Default
                  </span>
                )}
              </div>
              
              <div className="preview-content">
                <div className="preview-address">
                  <p>{formData.address_line_1 || 'Address Line 1'}</p>
                  {formData.address_line_2 && <p>{formData.address_line_2}</p>}
                  <p>
                    {formData.town || 'Town'}, {states.find(s => s.id === formData.state_id)?.name || 'State'} {formData.postcode || 'Postcode'}
                  </p>
                  <p>{countries.find(c => c.id === formData.country_id)?.name || 'Country'}</p>
                </div>

                <div className="preview-details">
                  <div className="preview-detail-row">
                    <User size={16} />
                    <span className="preview-label">User ID:</span>
                    <span className="preview-value">{formData.user_id || 'Not provided'}</span>
                  </div>
                </div>

                <div className="preview-badges">
                  <span className={`preview-badge ${formData.is_default ? 'default' : 'standard'}`}>
                    {formData.is_default ? 'Default Address' : 'Standard Address'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>📦 Address Guidelines</h4>
              <ul>
                <li>Ensure the user ID is a valid UUID</li>
                <li>Use complete street addresses</li>
                <li>Include apartment/unit numbers if applicable</li>
                <li>Verify postcode matches the selected area</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/delivery-addresses')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Create Address
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDeliveryAddress;
